// ============================================================================
// Connection Status Component
// ============================================================================
// Shows a banner when the network is offline or realtime subscriptions are
// degraded (auto-reconnecting). Hides automatically when healthy.
// ============================================================================

import { globalSubscriptionManager } from 'app/state/subscription-manager.js';

class ConnectionStatus {
    constructor() {
        this.element = null;
        this.hideTimeout = null;
        this.supabaseConnected = true;
        this.currentState = null;
        this.onOnline = () => this.update();
        this.onOffline = () => this.update();
    }

    init() {
        this.element = document.createElement('div');
        this.element.className = 'connection-status connection-status--hidden';
        this.element.id = 'connection-status';
        document.body.appendChild(this.element);

        window.addEventListener('online', this.onOnline);
        window.addEventListener('offline', this.onOffline);

        globalSubscriptionManager.setConnectionCallback((connected) => {
            this.supabaseConnected = connected;
            this.update();
        });

        if (!navigator.onLine) {
            this.show(false, 'No Internet Connection');
        }
    }

    update() {
        if (!navigator.onLine) {
            this.show(false, 'No Internet Connection');
        } else if (!this.supabaseConnected) {
            this.show(false, 'Reconnecting to live updates\u2026');
        } else if (this.currentState === 'false:No Internet Connection') {
            // Show "Connected" only when recovering from network loss (user cares)
            this.show(true, 'Connected');
        } else {
            // Subscription recovery: silently hide the banner (no "Connected" flash)
            this._hide();
        }
    }

    show(connected, message) {
        const stateKey = `${connected}:${message}`;
        if (this.currentState === stateKey) return;
        this.currentState = stateKey;

        clearTimeout(this.hideTimeout);

        this.element.className = `connection-status connection-status--${connected ? 'connected' : 'disconnected'}`;
        this.element.innerHTML = `
            <i class="fas fa-${connected ? 'check-circle' : 'exclamation-triangle'}"></i>
            <span>${message}</span>
        `;

        if (connected) {
            this.hideTimeout = setTimeout(() => {
                this.element.classList.add('connection-status--hidden');
            }, 3000);
        }
    }

    _hide() {
        clearTimeout(this.hideTimeout);
        this.currentState = null;
        this.element.className = 'connection-status connection-status--hidden';
    }

    cleanup() {
        clearTimeout(this.hideTimeout);
        window.removeEventListener('online', this.onOnline);
        window.removeEventListener('offline', this.onOffline);
        globalSubscriptionManager.setConnectionCallback(null);
        this.element?.remove();
        this.currentState = null;
    }
}

export const connectionStatus = new ConnectionStatus();
